import React, { useEffect, useState } from 'react';
import { followLink } from '@/navigation.js';
import { track } from '@/analytics.js';
import { setPageHead } from '@/page-head.js';
import { ContactDialog } from '@/components/ui/contact-dialog.js';
import { loadJSON } from '@/utils.js';
import { SUMMARY_URL, TDatasetSummary } from '@/utils/home.js';
import { getDatasetPath, HOME_PAGE } from '@/utils/seo.js';
import {
	Card,
	Count,
	Footer,
	Grid,
	GroupCard,
	GroupItem,
	Intro,
	Name,
	Page,
	Stage,
	Status,
	Top,
} from '@/home.styled.js';

// The datasets in their groups, in menu order: each group's title, then its datasets
const byGroup = (summary: TDatasetSummary[]) =>
	summary.reduce<{ title: string; datasets: TDatasetSummary[] }[]>((groups, dataset) => {
		const last = groups.at(-1);
		if (last?.title === dataset.group) last.datasets.push(dataset);
		else groups.push({ title: dataset.group, datasets: [dataset] });
		return groups;
	}, []);

const DatasetCard = ({ summary }: { summary: TDatasetSummary }) => {
	const href = getDatasetPath(summary.id);
	return (
		<Card
			href={href}
			onClick={followLink(href, () => track('home_card_opened', { dataset: summary.id }))}
		>
			<Stage $centred={summary.centred ?? false}>
				{summary.image && <img src={summary.image} alt={summary.imageItem ?? ''} loading="lazy" />}
			</Stage>
			<Name>{summary.title}</Name>
			<Count>{summary.count}</Count>
		</Card>
	);
};

// The id of the script element in which the worker sends the summary with a page load of the home
// page (worker/page.ts), beside the page rendered from it, so that the app shows the same cards at
// once instead of loading them again
export const SUMMARY_ELEMENT_ID = 'home-summary';

const readSentSummary = (): TDatasetSummary[] | null => {
	try {
		return JSON.parse(document.getElementById(SUMMARY_ELEMENT_ID)?.textContent ?? 'null');
	} catch {
		return null;
	}
};

type TProps = {
	// Given when the page is rendered at build time (scripts/build-seo.ts); sent with the page, or
	// else loaded, otherwise
	summary?: TDatasetSummary[];
};

/** The datasets as cards, each opening its dataset in the app. */
export const Home = ({ summary: given }: TProps) => {
	const [summary, setSummary] = useState(() => given ?? readSentSummary());
	const [failed, setFailed] = useState(false);
	const [contactOpen, setContactOpen] = useState(false);

	useEffect(() => {
		setPageHead(HOME_PAGE);
		if (summary) return;
		loadJSON<TDatasetSummary[]>(SUMMARY_URL).then(setSummary, () => setFailed(true));
		// Once: the summary only changes with a deploy
	}, []);

	return (
		<Page>
			<Top>
				<img src="/assets/brand/all2scale-logo.svg" alt="All2Scale" />
			</Top>
			<main>
				<Intro>
					<h1>See how big things really are</h1>
					<p>
						Pick a set and compare everything in it side by side, at its true scale: planets,
						skyscrapers, anime characters and more.
					</p>
				</Intro>
				{summary && (
					<Grid>
						{byGroup(summary).map((group) => (
							<React.Fragment key={group.title}>
								<GroupItem>
									<GroupCard>
										<h2>{group.title}</h2>
										<span>
											{group.datasets.length === 1
												? '1 set'
												: `${group.datasets.length} sets`}
										</span>
									</GroupCard>
								</GroupItem>
								{group.datasets.map((dataset) => (
									<li key={dataset.id}>
										<DatasetCard summary={dataset} />
									</li>
								))}
							</React.Fragment>
						))}
					</Grid>
				)}
				{!summary && (
					<Status role={failed ? 'alert' : 'status'}>
						{failed ? 'Could not load the sets.' : 'Loading…'}
					</Status>
				)}
			</main>
			<Footer>
				<p>Each set lists its full credits in the app. © 2026 All2Scale</p>
				<button type="button" onClick={() => setContactOpen(true)}>
					Contact
				</button>
				<ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />
			</Footer>
		</Page>
	);
};
